<template>
    <div class="question-header">
        <div class="meta-container">

            <div class="question-meta">
                <slot name="filter"></slot>
                <span class="badge badge-difficulty" :data-difficulty="difficulty">{{ formatTopic(difficulty) }}</span>
                <span class="badge badge-type">{{ formattedType }}</span>
                <span class="badge badge-marks" :data-marks="maxMarks">{{ maxMarks }} {{ maxMarks > 1 ? 'marks' : 'mark' }}</span>
    
                <div class="header-right" style="margin-left: auto; display: flex; align-items: center; gap: 16px;">
                    <!-- Remaining Questions Indicator (for non-premium users) -->
                    <div
                        v-if="userType !== 'premium' && remainingQuestions !== undefined"
                        class="remaining-questions"
                        :class="{ 'low': remainingQuestions <= 1 }"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M12 6v6l4 2"/>
                        </svg>
                        {{ remainingQuestions }} left today
                    </div>
                    <div class="stopwatch-display">
                        {{ formattedTime }}
                    </div>
                </div>
            </div>
            <div class="question-meta">
                <span
                    v-for="topic in topics"
                    :key="topic"
                    class="topic-tag"
                >
                    {{ getTopicLabel(topic) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { getTopicLabel } from '@/utils/topicData';

const props = defineProps({
    difficulty: String,
    questionType: String,
    maxMarks: Number,
    topics: {
        type: Array,
        default: () => []
    },
    formattedTime: String,
    remainingQuestions: {
        type: Number,
        default: undefined
    },
    userType: {
        type: String,
        default: 'guest'
    }
});

const formattedType = computed(() => {
    return props.questionType
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
});

const formatTopic = (topic) => {
    return topic
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
};
</script>

<style scoped>
.question-header {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid rgba(var(--accent-rgb), 0.4);
}

.meta-container {
    gap: 8px;
    display: flex;
    flex-direction: column;
}

.question-meta {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.badge {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
    background: var(--bg-secondary);
    color: var(--text-secondary);
    border: 1px solid rgba(var(--accent-rgb), 0.5);
    align-items: center;
    display: inline-flex;
}

.badge-difficulty {
    border-width: 1px;
    border-style: solid;
}

.badge-difficulty[data-difficulty="band_1"] {
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
    border-color: rgba(76, 175, 80, 0.5);
}

.badge-difficulty[data-difficulty="band_2"] {
    background: rgba(139, 195, 74, 0.2);
    color: #8bc34a;
    border-color: rgba(139, 195, 74, 0.5);;
}

.badge-difficulty[data-difficulty="band_3"] {
    background: rgba(255, 235, 59, 0.2);
    color: #ffeb3b;
    border-color: rgba(255, 235, 59, 0.5);;
}

.badge-difficulty[data-difficulty="band_4"] {
    background: rgba(255, 152, 0, 0.2);
    color: #ff9800;
    border-color: rgba(255, 152, 0, 0.5);
}

.badge-difficulty[data-difficulty="band_5"] {
    background: rgba(255, 87, 34, 0.2);
    color: #ff5722;
    border-color: rgba(255, 87, 34, 0.5);
}

.badge-difficulty[data-difficulty="band_6"] {
    background: rgba(244, 67, 54, 0.2);
    color: #f44336;
    border-color: rgba(244, 67, 54, 0.5);
}

.badge-marks {
    border-width: 1px;
    border-style: solid;
}

.badge-marks[data-marks="1"] {
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
    border-color: rgba(76, 175, 80, 0.5);
}

.badge-marks[data-marks="2"] {
    background: rgba(139, 195, 74, 0.2);
    color: #8bc34a;
    border-color: rgba(139, 195, 74, 0.5);
}

.badge-marks[data-marks="3"] {
    background: rgba(255, 235, 59, 0.2);
    color: #ffeb3b;
    border-color: rgba(255, 235, 59, 0.5);
}

.badge-marks[data-marks="4"] {
    background: rgba(255, 152, 0, 0.2);
    color: #ff9800;
    border-color: rgba(255, 152, 0, 0.5);
}

.badge-marks[data-marks="5"] {
    background: rgba(255, 87, 34, 0.2);
    color: #ff5722;
    border-color: rgba(255, 87, 34, 0.5);
}

.badge-marks[data-marks="6"],
.badge-marks[data-marks="7"],
.badge-marks[data-marks="8"],
.badge-marks[data-marks="9"],
.badge-marks[data-marks="10"],
.badge-marks[data-marks="11"],
.badge-marks[data-marks="12"] {
    background: rgba(244, 67, 54, 0.2);
    color: #f44336;
    border-color: rgba(244, 67, 54, 0.5);
}

.stopwatch-display {
    color: var(--text-primary);
}

.topic-tag {
    padding: 4px 10px;
    background: rgba(var(--text-primary-rgb), 0.1);
    font-size: 13px;
    color: var(--text-primary);
    opacity: 0.8;
    align-items: center;
    border: 1px solid rgba(var(--text-primary-rgb), 0.4);
    display: inline-flex;
}

.remaining-questions {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: rgba(var(--accent-rgb), 0.1);
    border: 1px solid rgba(var(--accent-rgb), 0.3);
    color: var(--accent);
    font-size: 12px;
    font-weight: 500;
}

.remaining-questions.low {
    background: rgba(244, 67, 54, 0.1);
    border-color: rgba(244, 67, 54, 0.3);
    color: #f44336;
}

.remaining-questions svg {
    opacity: 0.8;
}
</style>